import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "./context/AuthContext";
import {
  Calendar,
  CreditCard,
  ArrowRight,
  RefreshCw,
  FileText,
  User,
  MessageSquare,
  Settings,
} from "lucide-react";
import Topbar from "./components/Topbar";
import TopbarNonav from "./components/TopbarNonav";
import BillingHistory from "./components/BillingHistory";
import PremiumMessaging from "./components/PremiumMessaging";
import config from "./config/environment";
import { handleApiError, logError } from "./utils/errorHandler";

const Profile = () => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [isCancelingProcessActive, setIsCancelingProcessActive] =
    useState(false);

  const [reactivating, setReactivating] = useState(false);
  const [activeTab, setActiveTab] = useState("profile"); // New state for active tab
  const { userId, firstName, lastName, username, paidUser, isAuthenticated } =
    useContext(AuthContext);
  const navigate = useNavigate();

  // Navigation tabs configuration
  const navigationTabs = [
    {
      id: "profile",
      name: "Profile & Subscription",
      icon: User,
      description: "Manage your account and subscription",
    },
    {
      id: "billing",
      name: "Billing History",
      icon: FileText,
      description: "View your payment history and invoices",
    },
    {
      id: "messages",
      name: "Messages",
      icon: MessageSquare,
      description: "Premium messaging and notifications",
    },
  ];

  // Format currency values
  const formatCurrency = (amount, currency = "USD") => {
    if (!amount && amount !== 0) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Get subscription pricing from data JSON
  const getSubscriptionPricing = (subscription) => {
    if (!subscription || !subscription.data)
      return { total: "N/A", currency: "USD", interval: "month" };

    try {
      // Parse the data if it's a string
      const data =
        typeof subscription.data === "string"
          ? JSON.parse(subscription.data)
          : subscription.data;

      // Get total from the main totals or recurring_totals
      const total = data.recurring_totals?.total || data.totals?.total || 0;

      // Get currency code
      const currency = data.currency_code || "USD";

      // Get billing interval
      let interval = "month";
      if (data.items && data.items.length > 0) {
        interval = data.items[0].billing_cycle?.interval || "month";
      }

      return { total, currency, interval };
    } catch (err) {
      console.error("Error parsing subscription data:", err);
      return { total: "N/A", currency: "USD", interval: "month" };
    }
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      // Prevent fetching if a cancellation process is active
      if (isCancelingProcessActive) {
        console.log("Cancellation process active, skipping fetch.");
        return;
      }

      if (!userId) {
        console.log("Waiting for userId...");
        return;
      }

      try {
        setLoading(true);
        console.log("Fetching subscription data for userId:", userId);
        const response = await axios.get("/subapi/user-subscription", {
          params: { userId },
        });

        // Log the full response data for debugging
        console.log("Subscription API Response:", response.data);

        // Set the subscription data if available
        if (
          response.data.success &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          // We should only have one result now due to our backend query
          setSubscriptionData(response.data.data);
          console.log("Found subscription data:", response.data.data);

          // Log the subscription status specifically to help with debugging
          const subStatus = response.data.data[0]?.status;
          console.log("Current subscription status:", subStatus);
          if (subStatus === "canceling") {
            console.log("Subscription is currently in canceling state");
          }
        } else {
          console.log("No subscription data found");
          setSubscriptionData([]);
        }
      } catch (err) {
        console.error("Error fetching subscription:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [userId, isCancelingProcessActive]);

  // Ensure we properly get the latest subscription data
  let subscription = null;

  if (Array.isArray(subscriptionData) && subscriptionData.length > 0) {
    const subData = subscriptionData[0];
    // Only consider it a valid subscription if it has essential subscription fields
    if (
      subData &&
      (subData.status || subData.plan_type || subData.paddle_subscription_id)
    ) {
      subscription = subData;
    }
  } else if (!Array.isArray(subscriptionData) && subscriptionData) {
    // Handle non-array subscription data
    if (
      subscriptionData.status ||
      subscriptionData.plan_type ||
      subscriptionData.paddle_subscription_id
    ) {
      subscription = subscriptionData;
    }
  }

  // Log subscription details for debugging
  console.log("Raw subscriptionData:", subscriptionData);
  console.log("Processed subscription:", subscription);
  console.log("subscription is null?", subscription === null);
  console.log("subscription is undefined?", subscription === undefined);
  console.log("subscription is falsy?", !subscription);
  console.log("subscription status:", subscription?.status);
  console.log(
    "should show reactivate button?",
    subscription?.status === "canceled" || subscription?.status === "canceling"
  );

  // Make sure the cancel subscription function properly uses the subscription ID
  const handleCancelSubscription = async () => {
    // If already processing, don't allow another attempt
    if (cancelLoading) {
      console.log(
        "Cancel request already in progress, preventing duplicate request"
      );
      return;
    }

    // Start loading state
    setCancelLoading(true);

    try {
      // Get subscription ID
      const subscriptionId = subscription?.id;

      if (!subscriptionId) {
        setCancelError("No valid subscription ID found");
        setCancelLoading(false);
        return;
      }

      console.log("Starting subscription cancellation for ID:", subscriptionId);

      // IMPORTANT: Force local state update FIRST - don't wait for server
      if (subscription) {
        // Deep clone the subscription to avoid reference issues
        const forcedCancelingSubscription = JSON.parse(
          JSON.stringify(subscription)
        );
        // Force the status to "canceling"
        forcedCancelingSubscription.status = "canceling";

        // Update the local state directly
        if (Array.isArray(subscriptionData)) {
          const updatedData = subscriptionData.map((sub) =>
            sub.id === subscriptionId ? forcedCancelingSubscription : sub
          );
          setSubscriptionData(updatedData);
        } else {
          setSubscriptionData([forcedCancelingSubscription]);
        }

        // Log the forced update
        console.log('Forced local subscription status to "canceling"');
      }

      // Set success immediately for UI (don't wait for server)
      setCancelSuccess(true);

      // Now make the API call - but our UI already shows success
      console.log("Making API call to cancel subscription");
      await axios.post("/subapi/cancel-subscription", {
        userId,
        subscriptionId,
      });

      console.log("Cancellation API call completed");

      // Close the modal after a short delay
      setTimeout(() => {
        console.log("Closing cancellation modal");
        setCancelModalOpen(false);
      }, 1500);

      // Reload the page after a significant delay
      // to ensure database has time to update
      setTimeout(() => {
        console.log("Forcing page reload to get fresh data");
        window.location.reload();
      }, 5000);
    } catch (error) {
      console.error("Error cancelling subscription:", error);

      // Even if the API call fails, keep the UI showing "canceling"
      // since we've already committed to that state in the UI
      // This avoids confusion for the user

      // Show error message
      setCancelError(
        "The subscription has been marked for cancellation, but there was an issue contacting the server. " +
          "The page will refresh automatically to show the updated status."
      );

      // Still reload the page after a delay
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } finally {
      // Don't reset loading state - keep it loading until page reload
      // This prevents any further attempts
    }
  };

  // Handle subscription reactivation
  const handleReactivateSubscription = async () => {
    if (!userId || reactivating) return;

    setReactivating(true);
    try {
      // Log reactivation attempt with backend
      const response = await fetch(
        `${config.api.baseUrl}/api/subscriptions/reactivate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to log reactivation attempt");
      }

      const data = await response.json();

      // Redirect to pricing page for new subscription
      navigate("/pricing", {
        state: {
          isReactivation: true,
          previousPlan: data.previousPlan || subscription?.plan_type,
          message:
            "Welcome back! Choose a plan to reactivate your subscription.",
        },
      });
    } catch (error) {
      console.error("Error during reactivation:", error);
      const handledError = handleApiError(error);
      logError(handledError, { context: "subscription_reactivation", userId });

      // Still redirect to pricing even if logging fails
      navigate("/pricing", {
        state: {
          isReactivation: true,
          previousPlan: subscription?.plan_type,
          message: "Welcome back! Choose a plan to continue.",
        },
      });
    } finally {
      setReactivating(false);
    }
  };

  // Function to render profile content
  const renderProfileContent = () => {
    return (
      <>
        {/* User Profile Card */}
        <div className="flex flex-col md:flex-row items-start gap-6 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="h-24 w-24 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-semibold">
                {firstName && lastName
                  ? `${firstName.charAt(0)}${lastName.charAt(0)}`
                  : username?.charAt(0) || "?"}
              </div>
            </div>
          </div>

          <div className="flex-grow space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {firstName && lastName ? `${firstName} ${lastName}` : "User"}
              </h1>
              <p className="text-gray-500 flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {username}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Account Status
                </h3>
                <div className="flex items-center">
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium mr-2
                                      ${
                                        subscription?.status === "active"
                                          ? "bg-green-100 text-green-800"
                                          : subscription?.status === "trialing"
                                          ? "bg-blue-100 text-blue-800"
                                          : subscription?.status === "canceled"
                                          ? "bg-red-100 text-red-800"
                                          : subscription?.status === "canceling"
                                          ? "bg-orange-100 text-orange-800"
                                          : subscription?.status === "past_due"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                  >
                    {subscription?.status
                      ? subscription.status === "canceling"
                        ? "Canceling at Period End"
                        : subscription.status.charAt(0).toUpperCase() +
                          subscription.status.slice(1)
                      : "Free Plan"}
                  </span>
                  <span className="text-sm text-gray-700">
                    {subscription?.plan_type
                      ? subscription.plan_type === "monthly"
                        ? "Monthly Plan"
                        : subscription.plan_type === "yearly"
                        ? "Yearly Plan"
                        : subscription.plan_type
                      : "No Active Subscription"}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Subscription Details
                </h3>
                {subscription ? (
                  <>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">ID:</span>{" "}
                      {subscription.paddle_subscription_id || "Not Available"}
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">Valid Until:</span>{" "}
                      {subscription.ends_at
                        ? formatDate(subscription.ends_at)
                        : "Not Available"}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Status:</span> No active
                    subscription
                  </p>
                )}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Membership Information
              </h3>
              <p className="text-sm text-gray-700 mb-1">
                <span className="font-medium">
                  Subscription Membership created at:
                </span>{" "}
                {subscription?.created_at
                  ? formatDate(subscription.created_at)
                  : "No subscription created"}
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Management Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {subscription
              ? "Subscription Management"
              : "Get Started with SQL Guroo"}
          </h3>

          {subscription ? (
            <>
              {/* Subscription Header */}
              <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4">
                <div className="flex items-center">
                  <div className="mr-4 rounded-full bg-blue-100 p-2 text-blue-600">
                    <CreditCard className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {subscription?.plan_type === "monthly"
                        ? "SQL Guroo Monthly Plan"
                        : subscription?.plan_type === "yearly"
                        ? "SQL Guroo Yearly Plan"
                        : "SQL Guroo Premium"}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {subscription?.status
                        ? subscription.status.charAt(0).toUpperCase() +
                          subscription.status.slice(1)
                        : "Unknown"}
                    </p>
                  </div>
                </div>
                <div>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium
                                      ${
                                        subscription?.status === "active"
                                          ? "bg-green-100 text-green-800"
                                          : subscription?.status === "trialing"
                                          ? "bg-blue-100 text-blue-800"
                                          : subscription?.status === "past_due"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : subscription?.status === "canceled"
                                          ? "bg-red-100 text-red-800"
                                          : subscription?.status === "canceling"
                                          ? "bg-orange-100 text-orange-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                  >
                    {subscription?.status === "active"
                      ? "Active"
                      : subscription?.status === "trialing"
                      ? "Trial"
                      : subscription?.status === "past_due"
                      ? "Past Due"
                      : subscription?.status === "canceled"
                      ? "Canceled"
                      : subscription?.status === "canceling"
                      ? "Canceling at Period End"
                      : subscription?.status || "Unknown"}
                  </span>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Billing Details */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h5 className="mb-3 text-sm font-medium text-gray-500">
                    Billing
                  </h5>
                  <p className="mb-1 text-base font-semibold text-gray-900">
                    {(() => {
                      const pricing = getSubscriptionPricing(subscription);
                      return formatCurrency(pricing.total, pricing.currency);
                    })()}
                    <span className="ml-1 text-sm font-normal text-gray-500">
                      /{" "}
                      {(() => {
                        const pricing = getSubscriptionPricing(subscription);
                        return pricing.interval || "month";
                      })()}
                    </span>
                  </p>
                  {(subscription?.status === "active" ||
                    subscription?.status === "trialing") && (
                    <p className="text-sm text-gray-500">
                      Next billing date:{" "}
                      {subscription?.ends_at
                        ? formatDate(subscription.ends_at)
                        : "Not available"}
                    </p>
                  )}
                </div>

                {/* Period Details */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h5 className="mb-3 text-sm font-medium text-gray-500">
                    Current Period
                  </h5>
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                    <span>
                      {subscription?.starts_at && subscription?.ends_at
                        ? `${formatDate(subscription.starts_at)} - ${formatDate(
                            subscription.ends_at
                          )}`
                        : "Period information not available"}
                    </span>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h5 className="mb-3 text-sm font-medium text-gray-500">
                    Subscription Details
                  </h5>
                  <div className="flex items-center">
                    <div className="mr-3 rounded-md bg-white p-2 shadow-sm">
                      <svg
                        className="h-6 w-6"
                        viewBox="0 0 32 32"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="#3395FF"
                          d="M21.634 11.225H10.558c-.11 0-.221.007-.33.023a2.34 2.34 0 0 0-.875.261 2.444 2.444 0 0 0-.786.642 2.47 2.47 0 0 0-.412.83c-.045.18-.064.362-.057.545v3.353c-.007.184.012.367.057.546.08.293.224.563.412.794a2.45 2.45 0 0 0 .787.641c.133.07.272.126.415.166.14.04.281.073.423.1.167.017.335.026.502.026h11.12c.168 0 .336-.009.502-.026a2.485 2.485 0 0 0 1.66-.843 2.442 2.442 0 0 0 .465-.761c.046-.18.064-.362.058-.546v-3.356c.006-.183-.013-.366-.058-.545a2.47 2.47 0 0 0-.465-.761 2.468 2.468 0 0 0-.786-.642 2.405 2.405 0 0 0-.83-.26 3.134 3.134 0 0 0-.342-.022"
                        />
                        <path
                          fill="#FFF"
                          d="M13.828 15.921h-1.36a.189.189 0 0 1-.188-.172l-.673-4.437a.142.142 0 0 1 .145-.164h1.36c.095 0 .174.07.188.164l.673 4.437a.142.142 0 0 1-.145.172m3.252-4.601h-1.287a.187.187 0 0 0-.152.77l-1.846 2.84-.78-2.783a.19.19 0 0 0-.18-.134h-1.265a.144.144 0 0 0-.137.188l1.462 4.218-1.374 1.958c-.43.061-.1.126 0 .181a.145.145 0 0 0 .118.053h1.288a.189.189 0 0 0 .152-.077l4.426-6.366c.042-.62.008-.127 0-.182a.145.145 0 0 0-.119-.053m2.436 4.601h-1.214a.19.19 0 0 1-.188-.172l-.673-4.437a.142.142 0 0 1 .145-.164h1.23c.095 0 .174.07.188.164l.657 4.313a.142.142 0 0 1-.145.172"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {subscription?.plan_type === "monthly"
                          ? "Monthly Plan"
                          : subscription?.plan_type === "yearly"
                          ? "Yearly Plan"
                          : "SQL Guroo Premium"}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {subscription?.paddle_subscription_id || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* No Subscription Content */
            <div className="text-center py-8">
              <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                No Active Subscription
              </h4>
              <p className="text-sm text-gray-500 mb-6">
                Get started with SQL Guroo Premium to access all features and
                advance your SQL skills.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3">
              {/* Reactivate button for cancelled/canceling subscriptions */}
              {(subscription?.status === "canceled" ||
                subscription?.status === "canceling") && (
                <button
                  onClick={handleReactivateSubscription}
                  className="inline-flex items-center rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 focus:outline-none focus:ring-4 focus:ring-green-500 focus:ring-offset-2"
                  disabled={reactivating || cancelLoading}
                >
                  {reactivating ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reactivate Subscription
                    </>
                  )}
                </button>
              )}

              {/* Buy Plan button for users without subscriptions */}
              {!subscription && (
                <button
                  onClick={() => navigate("/pricing")}
                  className="inline-flex items-center rounded-lg border border-blue-300 bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-offset-2"
                  disabled={cancelLoading}
                >
                  Buy Subscription
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              )}

              {/* Cancel button only for active subscriptions */}
              {subscription && subscription.status === "active" && (
                <button
                  onClick={() => {
                    if (cancelLoading) return; // Prevent click during loading
                    setCancelModalOpen(true);
                  }}
                  disabled={
                    subscription?.status === "canceling" || cancelLoading
                  }
                  className={`inline-flex items-center rounded-lg border border-red-300 px-4 py-2 text-sm font-medium 
                                        ${
                                          subscription?.status ===
                                            "canceling" || cancelLoading
                                            ? "bg-gray-100 text-gray-500 cursor-not-allowed opacity-70"
                                            : "bg-white text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                        }`}
                >
                  {cancelLoading ? (
                    <>
                      <svg
                        className="inline w-4 h-4 mr-2 text-gray-500 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : subscription?.status === "canceling" ? (
                    "Cancellation Pending"
                  ) : (
                    "Cancel at Period End"
                  )}
                </button>
              )}
            </div>

            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center rounded-lg bg-gray-100 p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
              disabled={cancelLoading}
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      </>
    );
  };

  if (loading)
    return (
      <div className="text-center py-10">
        Loading subscription information...
      </div>
    );
  if (error)
    return <div className="text-center py-10 text-red-600">Error: {error}</div>;

  return (
    <div className="relative flex min-h-screen justify-center bg-gray-50">
      <div
        className="relative flex flex-col 
                w-full 
            max-w-[98%]
            2xl:max-w-[1820px]
                mx-auto 
            min-h-screen 
            overflow-y-auto"
      >
        <TopbarNonav />
        <div className="container mx-auto px-4 py-6 mb-20">
          {/* Main Content with Sidebar */}
          <div className="flex gap-6">
            {/* Left Sidebar Navigation */}
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Account Settings
                </h2>
                <nav className="space-y-2">
                  {navigationTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? "bg-blue-50 text-blue-700 border border-blue-200"
                            : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <Icon
                          className={`w-5 h-5 mr-3 ${
                            activeTab === tab.id
                              ? "text-blue-600"
                              : "text-gray-400"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{tab.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {tab.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 min-w-0">
              {activeTab === "profile" && (
                <div className="space-y-6">
                  {/* Profile Content */}
                  {renderProfileContent()}
                </div>
              )}

              {activeTab === "billing" && (
                <div className="space-y-6">
                  {/* Billing History Content */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">
                      Billing History
                    </h2>
                    <BillingHistory />
                  </div>
                </div>
              )}

              {activeTab === "messages" && (
                <div className="space-y-6">
                  {/* Messages Content */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">
                      Messages
                    </h2>
                    <PremiumMessaging subscription={subscription} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cancel Subscription Modal */}
        {subscription && (
          <div
            id="cancelSubscriptionModal"
            className={`fixed ${
              cancelModalOpen ? "flex" : "hidden"
            } inset-0 z-50 justify-center items-center p-4 overflow-x-hidden overflow-y-auto bg-gray-900 bg-opacity-50`}
          >
            <div className="relative w-full max-w-md max-h-full">
              <div className="relative bg-white rounded-lg shadow">
                <div className="p-6 text-center">
                  <button
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5"
                    onClick={() => {
                      // Only allow closing if not in loading state
                      if (!cancelLoading) {
                        setCancelModalOpen(false);
                      }
                    }}
                    disabled={cancelLoading}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </button>

                  {cancelSuccess || subscription.status === "canceling" ? (
                    <div className="text-center">
                      <svg
                        className="mx-auto mb-4 w-14 h-14 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                      <h3 className="mb-5 text-lg font-normal text-gray-500">
                        Subscription Cancellation Scheduled
                      </h3>
                      <p className="mb-4 text-sm text-gray-500">
                        Your subscription will remain active until{" "}
                        {formatDate(subscription?.ends_at)}, after which it will
                        be canceled. You'll continue to have full access until
                        then.
                      </p>
                      {cancelError && (
                        <div
                          className="p-4 mt-4 mb-4 text-sm text-orange-700 bg-orange-100 rounded-lg"
                          role="alert"
                        >
                          {cancelError}
                        </div>
                      )}
                      {cancelLoading && (
                        <div className="my-4 flex items-center justify-center">
                          <svg
                            className="inline w-4 h-4 mr-2 text-gray-500 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span className="text-sm text-gray-500">
                            Updating status...
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          // Only allow closing if not in loading state
                          if (!cancelLoading) {
                            setCancelModalOpen(false);
                          }
                        }}
                        disabled={cancelLoading}
                        type="button"
                        className={`px-4 py-2 text-sm font-medium rounded-lg 
                                          ${
                                            cancelLoading
                                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                              : "bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-4 focus:outline-none focus:ring-gray-300"
                                          }`}
                      >
                        {cancelLoading ? "Please wait..." : "Close"}
                      </button>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="mx-auto mb-4 w-14 h-14 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <h3 className="mb-3 text-lg font-normal text-gray-500">
                        Are you sure you want to cancel your subscription?
                      </h3>
                      <p className="mb-5 text-sm text-gray-500">
                        Your subscription will remain active until the current
                        billing period ends on{" "}
                        {formatDate(subscription?.ends_at)}. You'll continue to
                        have access to all features until then.
                      </p>

                      <div className="mb-5 text-left p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Subscription Details
                        </h4>
                        <p className="text-sm text-gray-500 mb-1">
                          <span className="font-medium">Plan:</span>{" "}
                          {subscription?.plan_type === "monthly"
                            ? "Monthly"
                            : "Yearly"}{" "}
                          Plan
                        </p>
                        <p className="text-sm text-gray-500 mb-1">
                          <span className="font-medium">ID:</span>{" "}
                          {subscription?.paddle_subscription_id ||
                            subscription?.id ||
                            "N/A"}
                        </p>
                        <p className="text-sm text-gray-500">
                          <span className="font-medium">Valid until:</span>{" "}
                          {subscription?.ends_at
                            ? formatDate(subscription.ends_at)
                            : "N/A"}
                        </p>
                      </div>

                      {cancelError && (
                        <div
                          className="p-4 mb-5 text-sm text-red-700 bg-red-100 rounded-lg"
                          role="alert"
                        >
                          {cancelError}
                        </div>
                      )}

                      <div className="flex justify-center gap-4">
                        <button
                          onClick={() => setCancelModalOpen(false)}
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-gray-500 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200"
                          disabled={cancelLoading}
                        >
                          No, keep subscription
                        </button>
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancelLoading}
                          type="button"
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 disabled:opacity-70"
                        >
                          {cancelLoading ? (
                            <>
                              <svg
                                className="inline w-4 h-4 mr-2 text-white animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            "Yes, cancel subscription"
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
